import { Injectable } from '@angular/core';
import { BaseProvider } from './base';

export interface HealthCheckLicenseStatus {
    licenseStatus: string;
    licenseExpirationDate: string;
}

export interface LicensedFeatures {
    classifierDataset: boolean;
    anomalyDataset: boolean;
    detectorDataset: boolean;
}

export interface HealthCheckStatus {
    brainBank: boolean;
    sdkService: boolean;
    detectionService: boolean;
    cudaUpToDate: boolean | undefined; // Undefined represents the non-CUDA state
    license?: HealthCheckLicenseStatus;
    isGPU: boolean;
    features: LicensedFeatures;
}

export interface InspectorStatus {
    isBusy: boolean;
}

@Injectable()
export class OnPremiseProvider extends BaseProvider {
    private isOnPremise: boolean | null = null;
    private ipc: any | null = null;
    private healthCheckStatus: HealthCheckStatus | undefined;

    constructor() {
        super();
        if (this.isOnPremiseElectronApp()) {
            // Load a subset of the ipcRenderer.
            // For security purposes, we should only expose the methods we're actually using in the codebase;
            // please see on-premise/www/preload.ts.
            this.ipc = (window as any).api.ipcRenderer;
        }
    }

    public getBrainBankHostAddress(): string {
        return this.ipc.sendSync('get-brainbank-host-address');
    }

    public async healthcheck(): Promise<HealthCheckStatus> {
        if (this.isOnPremiseElectronApp()) {
            const result = await this.ipc.invoke('healthcheck');

            this.healthCheckStatus = {
                sdkService: result.sdkService,
                detectionService: result.detectionService,
                brainBank: result.brainBank,
                isGPU: result.isGPU,
                cudaUpToDate: result.cudaUpToDate,
                license: {
                    licenseStatus: result.license.licenseStatus,
                    licenseExpirationDate: result.license.licenseExpirationDate
                },
                features: {
                    classifierDataset: result.features.classifierDataset,
                    anomalyDataset: result.features.anomalyDataset,
                    detectorDataset: result.features.detectorDataset
                }
            };
        } else {
            // return healthy status when running in cloud
            this.healthCheckStatus = {
                sdkService: true,
                detectionService: true,
                brainBank: true,
                isGPU: true,
                cudaUpToDate: undefined,
                license: undefined,
                features: {
                    classifierDataset: true,
                    anomalyDataset: true,
                    detectorDataset: true
                }
            };
        }

        return this.healthCheckStatus;
    }

    public async getInspectorStatus(): Promise<InspectorStatus> {
        if (this.isOnPremiseElectronApp()) {
            const result = await this.ipc.invoke('inspectorStatus');

            return {
                isBusy: result.isBusy
            };
        } else {
            // return not busy when running in cloud
            return {
                isBusy: false
            };
        }
    }

    public async getIsGPU(): Promise<boolean> {
        if (this.healthCheckStatus == undefined) {
            const result = await this.healthcheck();
            return result.isGPU;
        }

        return this.healthCheckStatus.isGPU;
    }

    public async getLicenseExpirationDate(): Promise<string> {
        if (this.healthCheckStatus == undefined) {
            const result = await this.healthcheck();
            return result.license ? result.license.licenseExpirationDate : '';
        }

        return this.healthCheckStatus.license ? this.healthCheckStatus.license.licenseExpirationDate : '';
    }

    public async getLicensedFeatures(): Promise<LicensedFeatures> {
        if (this.healthCheckStatus == undefined) {
            const result = await this.healthcheck();
            return result.features;
        }

        return this.healthCheckStatus.features;
    }

    public subscribeForHealthCheck(listener: (status: any) => void) {
        if (this.isOnPremiseElectronApp()) {
            this.ipc.on(`healthcheck`, (event: any, status: any) => {
                listener(status);
            });
        }
    }

    public subscribeForInspectorStatus(listener: (status: any) => void) {
        if (this.isOnPremiseElectronApp()) {
            this.ipc.on('inspectorStatus', (event: any, status: any) => {
                listener(status);
            });
        }
    }

    public isOnPremiseElectronApp(): boolean {
        if (this.isOnPremise != null) {
            return this.isOnPremise;
        }

        if (navigator && navigator.userAgent && navigator.userAgent.toLowerCase().indexOf('electron') >= 0) {
            this.isOnPremise = true;
        } else {
            this.isOnPremise = false;
        }

        return this.isOnPremise;
    }

    public async getWheatleyToken(): Promise<string> {
        return await this.ipc.invoke('get-wheatley-token');
    }

    public async openLinkInExternalBrowser(link: string): Promise<void> {
        return await this.ipc.invoke('open-link-in-external-browser', link);
    }

    public async setWheatleyToken(value: string): Promise<void> {
        await this.ipc.invoke('set-wheatley-token', value);
    }

    public async deleteWheatleyToken(): Promise<void> {
        await this.ipc.invoke('delete-wheatley-token');
    }

    public async downloadUrl(url: string): Promise<void> {
        await this.ipc.invoke('download-url', url);
    }

    public getAppVersion(): string {
        return this.isOnPremiseElectronApp() ? this.ipc.sendSync('get-app-version') : '1.0.0';
    }

    public async getDownloadPath(): Promise<string> {
        return await this.ipc.invoke('get-download-path');
    }

    public async archiveLogs(downloadPath: string): Promise<boolean> {
        return await this.ipc.invoke('export-logs', downloadPath);
    }

    public maximize(): void {
        return this.ipc.sendSync('app-header-action-maximize');
    }
    public minimize(): void {
        return this.ipc.sendSync('app-header-action-minimize');
    }
    public close(): void {
        return this.ipc.sendSync('app-header-action-close');
    }
}



// WEBPACK FOOTER //
// ./src/providers/on-premise.ts